import classNames from "classnames";
import React from "react";

/**
 * Enum matching the C# DecorativeIconColor
 */
export enum DecorativeIconColor {
  DarkBlue = "DarkBlue",
  LightBlue = "LightBlue",
  OutlineBlue = "OutlineBlue",
  OutlineWhite = "OutlineWhite",
  Neutral = "Neutral",
  Yellow = "Yellow",
  Green = "Green",
  Green200 = "Green200",
  Pink400 = "Pink400",
  Pink600 = "Pink600",
  Red = "Red",
  Turquoise = "Turquoise",
  PetrolDark = "PetrolDark",
  PetrolLight = "PetrolLight",
  Petrol200 = "Petrol200",
  Success = "Success",
  Information = "Information"
}

/**
 * Enum matching the C# DecorativeIconSize
 */
export enum DecorativeIconSize {
  Xs = "Xs",
  Sm = "Sm",
  Lg = "Lg",
  Xl = "Xl"
}

export interface DecorativeIconProps {
  /** Symbol/icon name (e.g., "hospital", "info") */
  symbol: string;
  /** Color scheme for the icon */
  color?: DecorativeIconColor;
  /** Size of the icon */
  size?: DecorativeIconSize;
  /** Responsive size for SM breakpoint */
  sizeSm?: DecorativeIconSize;
  /** Responsive size for MD breakpoint */
  sizeMd?: DecorativeIconSize;
  /** Responsive size for LG breakpoint */
  sizeLg?: DecorativeIconSize;
  /** Responsive size for XL breakpoint */
  sizeXl?: DecorativeIconSize;
  /** Optional title for accessibility */
  title?: string;
  /** Additional CSS classes */
  additionalClassName?: string;
}

/**
 * DecorativeIcon component - React equivalent of the Razor DecorativeIcon component.
 *
 * Displays an SVG icon with a colored background, border, and rounded corners.
 * Matches the styling from `Features/Shared/Assets/DecorativeIcon/DecorativeIcon.cshtml`.
 *
 * **Features:**
 * - Multiple color schemes matching C# DecorativeIconColor enum
 * - Three sizes: Sm (50px), Lg (100px), Xl (200px)
 * - Responsive sizing at different breakpoints
 * - SVG fills the entire container
 * - Proper accessibility attributes
 *
 * @example
 * ```tsx
 * // Basic usage
 * <DecorativeIcon symbol="hospital" color={DecorativeIconColor.Red} />
 *
 * // With responsive sizing
 * <DecorativeIcon
 *   symbol="info"
 *   color={DecorativeIconColor.Information}
 *   size={DecorativeIconSize.Sm}
 *   sizeLg={DecorativeIconSize.Lg}
 * />
 * ```
 */
export function DecorativeIcon({
  symbol,
  color = DecorativeIconColor.Information,
  size = DecorativeIconSize.Xs,
  sizeSm,
  sizeMd,
  sizeLg,
  sizeXl,
  title,
  additionalClassName = ""
}: DecorativeIconProps) {
  // Build the icon href
  const href = `/svg/decoration/${symbol}.svg`;

  // Color classes - matches DecorativeIcon.cshtml
  const colorClassName = classNames({
    "text-light-blue-600 border-dark-blue bg-dark-blue":
      color === DecorativeIconColor.DarkBlue,
    "text-dark-blue border-light-blue-800 bg-light-blue-800":
      color === DecorativeIconColor.LightBlue,
    "text-dark-blue border-dark-blue": color === DecorativeIconColor.OutlineBlue,
    "text-neutral-0 border-neutral-0": color === DecorativeIconColor.OutlineWhite,
    "text-dark-blue border-neutral-200 bg-neutral-200":
      color === DecorativeIconColor.Neutral,
    "text-dark-blue border-yellow-800 bg-yellow-800":
      color === DecorativeIconColor.Yellow,
    "text-dark-blue border-green-200 bg-green-200":
      color === DecorativeIconColor.Green200,
    "text-dark-blue border-green-600 bg-green-600": color === DecorativeIconColor.Green,
    "text-dark-blue border-green-800 bg-green-800": color === DecorativeIconColor.Success,
    "text-dark-blue border-pink-400 bg-pink-400": color === DecorativeIconColor.Pink400,
    "text-dark-blue border-pink-600 bg-pink-600": color === DecorativeIconColor.Pink600,
    "text-neutral-0 border-pink-1200 bg-pink-1200": color === DecorativeIconColor.Red,
    "text-dark-blue border-turquoise-600 bg-turquoise-600":
      color === DecorativeIconColor.Turquoise,
    "text-dark-blue border-turquoise-800 bg-turquoise-800":
      color === DecorativeIconColor.Information,
    "text-neutral-0 border-petrol-900 bg-petrol-900":
      color === DecorativeIconColor.PetrolDark,
    "text-dark-blue border-petrol-400 bg-petrol-400":
      color === DecorativeIconColor.PetrolLight,
    "text-dark-blue border-petrol-200 bg-petrol-200":
      color === DecorativeIconColor.Petrol200
  });

  // Size classes - matches DecorativeIcon.cshtml
  const sizeClassName = classNames({
    // Base size
    "w-[3.125rem] h-[3.125rem]": size === DecorativeIconSize.Xs,
    "w-[4rem] h-[4rem] p-[0.1875rem]": size === DecorativeIconSize.Sm,
    "w-[6.25rem] h-[6.25rem]": size === DecorativeIconSize.Lg,
    "w-[12.5rem] h-[12.5rem]": size === DecorativeIconSize.Xl,

    // SM breakpoint
    "sm:w-[3.125rem] sm:h-[3.125rem]": sizeSm === DecorativeIconSize.Xs,
    "sm:w-[4rem] sm:h-[4rem] sm:p-[0.1875rem]": sizeSm === DecorativeIconSize.Sm,
    "sm:w-[6.25rem] sm:h-[6.25rem]": sizeSm === DecorativeIconSize.Lg,
    "sm:w-[12.5rem] sm:h-[12.5rem]": sizeSm === DecorativeIconSize.Xl,

    // MD breakpoint
    "md:w-[3.125rem] md:h-[3.125rem]": sizeMd === DecorativeIconSize.Xs,
    "md:w-[4rem] md:h-[4rem] md:p-[0.1875rem]": sizeMd === DecorativeIconSize.Sm,
    "md:w-[6.25rem] md:h-[6.25rem]": sizeMd === DecorativeIconSize.Lg,
    "md:w-[12.5rem] md:h-[12.5rem]": sizeMd === DecorativeIconSize.Xl,

    // LG breakpoint
    "lg:w-[3.125rem] lg:h-[3.125rem]": sizeLg === DecorativeIconSize.Xs,
    "lg:w-[4rem] lg:h-[4rem] lg:p-[0.1875rem]": sizeLg === DecorativeIconSize.Sm,
    "lg:w-[6.25rem] lg:h-[6.25rem]": sizeLg === DecorativeIconSize.Lg,
    "lg:w-[12.5rem] lg:h-[12.5rem]": sizeLg === DecorativeIconSize.Xl,

    // XL breakpoint
    "xl:w-[3.125rem] xl:h-[3.125rem]": sizeXl === DecorativeIconSize.Xs,
    "xl:w-[4rem] xl:h-[4rem] xl:p-[0.1875rem]": sizeXl === DecorativeIconSize.Sm,
    "xl:w-[6.25rem] xl:h-[6.25rem]": sizeXl === DecorativeIconSize.Lg,
    "xl:w-[12.5rem] xl:h-[12.5rem]": sizeXl === DecorativeIconSize.Xl
  });

  const borderRadiusClassName = classNames({
    // Base size
    "rounded-tl-br-md": size === DecorativeIconSize.Xs,
    "rounded-tl-br-lg": size === DecorativeIconSize.Sm || size === DecorativeIconSize.Lg,
    "rounded-tl-br-xl": size === DecorativeIconSize.Xl,

    // SM breakpoint
    "sm:rounded-tl-br-md": sizeSm === DecorativeIconSize.Xs,
    "sm:rounded-tl-br-lg":
      sizeSm === DecorativeIconSize.Sm || sizeSm === DecorativeIconSize.Lg,
    "sm:rounded-tl-br-xl": sizeSm === DecorativeIconSize.Xl,

    // MD breakpoint
    "md:rounded-tl-br-md": sizeMd === DecorativeIconSize.Xs,
    "md:rounded-tl-br-lg":
      sizeMd === DecorativeIconSize.Sm || sizeMd === DecorativeIconSize.Lg,
    "md:rounded-tl-br-xl": sizeMd === DecorativeIconSize.Xl,

    // LG breakpoint
    "lg:rounded-tl-br-md": sizeLg === DecorativeIconSize.Xs,
    "lg:rounded-tl-br-lg":
      sizeLg === DecorativeIconSize.Sm || sizeLg === DecorativeIconSize.Lg,
    "lg:rounded-tl-br-xl": sizeLg === DecorativeIconSize.Xl,

    // XL breakpoint
    "xl:rounded-tl-br-md": sizeXl === DecorativeIconSize.Xs,
    "xl:rounded-tl-br-lg":
      sizeXl === DecorativeIconSize.Sm || sizeXl === DecorativeIconSize.Lg,
    "xl:rounded-tl-br-xl": sizeXl === DecorativeIconSize.Xl
  });

  return (
    <div className={classNames("flex flex-none", sizeClassName, additionalClassName)}>
      <div
        className={classNames(
          "flex justify-center items-center border",
          borderRadiusClassName,
          colorClassName
        )}
      >
        <svg aria-hidden="true" className="w-full h-full" focusable="false">
          {title && <title>{title}</title>}
          <use href={`${href}#icon`} />
        </svg>
      </div>
    </div>
  );
}

export default DecorativeIcon;
